// src/components/portal/PortalLayout.js — Persistent shell for the partner
// portal: a collapsible sidebar + main content area, structured like the
// internal app's Layout but with only the nav a partner needs (Mandates,
// Contacts, Settings) — no Dashboard/Firms/BD Pipeline/Interactions, and
// this Contacts is the partner's own scoped view (PortalContacts.js), not
// the internal app's full Contacts page.
// Reuses the same global CSS classes (.sidebar-nav, .mobile-topbar, etc.)
// as the internal shell, so mobile collapse/slide-in behavior comes free.
import { useState, useEffect } from 'react'
import PortalSearch from './PortalSearch'
import { NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { PortalThemeProvider, usePortalTheme } from '../../context/PortalThemeContext'
import AccountSwitcher from '../AccountSwitcher'
import { usePortalDashboard, newAlertCount } from '../../pages/portal/PortalDashboard'

const SIDEBAR_COLLAPSED_KEY = 'rga_portal_sidebar_collapsed'
const SIDEBAR_FULL_W = 232
const SIDEBAR_MINI_W = 52

// Pages whose header holds PortalSearch beside the title.
const SEARCH_IN_HEADER = ['/portal', '/portal/mandates', '/portal/contacts', '/portal/submit-deal', '/portal/settings']

const Icons = {
  dashboard: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="9" rx="1"/><rect x="14" y="3" width="7" height="5" rx="1"/>
      <rect x="14" y="12" width="7" height="9" rx="1"/><rect x="3" y="16" width="7" height="5" rx="1"/>
    </svg>
  ),
  deal: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9"/><path d="M12 8v8M8 12h8"/>
    </svg>
  ),
  mandates: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 7h-3V5a2 2 0 0 0-2-2h-6a2 2 0 0 0-2 2v2H4a1 1 0 0 0-1 1v11a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8a1 1 0 0 0-1-1z"/>
      <path d="M9 7V5h6v2"/>
      <path d="M3 12h18"/>
    </svg>
  ),
  investors: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  settings: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  ),
  hamburger: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
    </svg>
  ),
  close: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  chevronLeft: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6"/>
    </svg>
  ),
  chevronRight: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6"/>
    </svg>
  ),
  signOut: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
      <polyline points="16 17 21 12 16 7"/>
      <line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
  sun: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4"/>
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/>
    </svg>
  ),
  moon: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  ),
}

function ThemeToggleButton({ theme, toggleTheme, className, style }) {
  return (
    <button
      onClick={toggleTheme}
      title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
      className={className}
      style={{
        background: 'transparent',
        border: '1px solid var(--border)',
        borderRadius: '6px',
        color: 'var(--text-secondary)',
        cursor: 'pointer',
        padding: '6px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transition: 'background 0.12s, color 0.12s, border-color 0.12s',
        ...style,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.background = 'var(--hover-tint)'
        e.currentTarget.style.color = 'var(--text-primary)'
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = 'transparent'
        e.currentTarget.style.color = 'var(--text-secondary)'
      }}
    >
      {theme === 'light' ? Icons.moon : Icons.sun}
    </button>
  )
}

function NavItem({ to, icon, label, end, collapsed, onClick, count = 0 }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      title={collapsed ? label : undefined}
      className="portal-navitem"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        gap: '10px',
        padding: collapsed ? '10px 0' : '8px 12px',
        borderRadius: '6px',
        fontSize: '13px',
        userSelect: 'none',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ opacity: 0.8, flexShrink: 0, position: 'relative' }}>
        {icon}
        {collapsed && count > 0 && (
          <span style={{ position: 'absolute', top: -3, right: -4, width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)' }} />
        )}
      </span>
      {!collapsed && label}
      {!collapsed && count > 0 && (
        <span className="badge badge-blue" style={{ fontSize: 10, marginLeft: 'auto' }}>{count}</span>
      )}
    </NavLink>
  )
}

function Wordmark({ collapsed }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
      <div style={{
        width: '28px', height: '28px',
        background: 'var(--accent)', borderRadius: '6px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
        </svg>
      </div>
      {!collapsed && (
        <div style={{ overflow: 'hidden' }}>
          <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', letterSpacing: '0.01em', whiteSpace: 'nowrap' }}>
            RGA CRM
          </div>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: '500', marginTop: '1px', whiteSpace: 'nowrap' }}>
            Partner Portal
          </div>
        </div>
      )}
    </div>
  )
}

function PortalLayoutInner({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true' }
    catch { return false }
  })
  const location = useLocation()
  const { user, profile, signOut } = useAuth()
  const { theme, toggleTheme } = usePortalTheme()

  useEffect(() => { setSidebarOpen(false) }, [location.pathname])

  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [sidebarOpen])

  function toggleCollapsed() {
    setCollapsed(c => {
      const next = !c
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next)) } catch {}
      return next
    })
  }

  const closeNav = () => setSidebarOpen(false)
  // New alerts since the partner last opened the dashboard (migrations/083).
  const { data: dashboard } = usePortalDashboard({ enabled: profile?.role === 'partner' })
  const alertCount = newAlertCount(dashboard)
  const sidebarW = collapsed ? SIDEBAR_MINI_W : SIDEBAR_FULL_W

  return (
    <div data-portal-theme={theme} style={{ display: 'flex', height: '100%', overflow: 'hidden', position: 'relative' }}>

      {/* ── Mobile top bar ───────────────────────────────────────────── */}
      <div className="mobile-topbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', overflow: 'hidden' }}>
          <button
            onClick={() => setSidebarOpen(true)}
            style={{
              background: 'transparent', border: 'none', color: 'var(--text-secondary)',
              cursor: 'pointer', padding: '6px', borderRadius: '6px',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}
          >
            {Icons.hamburger}
          </button>
          <Wordmark collapsed={false} />
        </div>
        <ThemeToggleButton theme={theme} toggleTheme={toggleTheme} />
      </div>

      {/* ── Mobile backdrop ──────────────────────────────────────────── */}
      {sidebarOpen && <div className="sidebar-backdrop" onClick={closeNav} />}

      {/* ── Sidebar ──────────────────────────────────────────────────── */}
      <aside
        className={`sidebar-nav${sidebarOpen ? ' sidebar-open' : ''}`}
        style={{
          width: `${sidebarW}px`,
          flexShrink: 0,
          background: 'var(--sidebar-bg)',
          borderRight: '1px solid var(--border)',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
          transition: 'width 0.25s ease, background-color 0.2s ease, border-color 0.2s ease',
          position: 'relative',
        }}
      >
        <div style={{
          padding: collapsed ? '20px 0 16px' : '20px 16px 16px',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          transition: 'padding 0.25s ease',
        }}>
          <Wordmark collapsed={collapsed} />
          <button className="sidebar-close-btn" onClick={closeNav}>
            {Icons.close}
          </button>
        </div>

        <nav style={{
          padding: collapsed ? '12px 6px' : '12px 8px',
          flex: 1,
          overflow: 'auto',
          transition: 'padding 0.25s ease',
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
            <NavItem to="/portal" end icon={Icons.dashboard} label="Dashboard" collapsed={collapsed} onClick={closeNav} count={alertCount} />
            <NavItem to="/portal/mandates" icon={Icons.mandates} label="Mandates" collapsed={collapsed} onClick={closeNav} />
            <NavItem to="/portal/contacts" icon={Icons.investors} label="Contacts" collapsed={collapsed} onClick={closeNav} />
            <NavItem to="/portal/submit-deal" icon={Icons.deal} label="Submit a Deal" collapsed={collapsed} onClick={closeNav} />
            <NavItem to="/portal/settings" icon={Icons.settings} label="Settings" collapsed={collapsed} onClick={closeNav} />
          </div>
        </nav>

        {/* Account switcher (Travis/Jean only) and theme toggle sit above the bottom (email/sign-out) block, not
            fixed over the page content -- above the sign-out button when
            collapsed, above this block's own top border when expanded. */}
        <div style={{
          padding: collapsed ? '0 6px 8px' : '0 16px 8px',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          alignItems: collapsed ? 'center' : 'flex-start',
        }}>
          <AccountSwitcher collapsed={collapsed} />
          <ThemeToggleButton theme={theme} toggleTheme={toggleTheme} style={collapsed ? { width: '34px', justifyContent: 'center' } : undefined} />
        </div>

        <div style={{
          padding: collapsed ? '12px 6px' : '12px 16px',
          borderTop: '1px solid var(--border)',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          transition: 'padding 0.25s ease',
          alignItems: collapsed ? 'center' : 'stretch',
        }}>
          {!collapsed && (profile?.name || user?.email) && (
            <div style={{
              fontSize: '11px', color: 'var(--text-secondary)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }} title={profile?.name || user.email}>
              {profile?.name || user.email}
            </div>
          )}
          <button
            onClick={signOut}
            title={collapsed ? 'Sign Out' : undefined}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start',
              gap: '8px', background: 'transparent', border: '1px solid var(--border)',
              borderRadius: '6px', padding: collapsed ? '7px' : '6px 10px',
              color: 'var(--text-secondary)', fontSize: '12px', cursor: 'pointer',
              fontFamily: 'inherit', transition: 'background 0.12s, color 0.12s, border-color 0.12s',
              width: collapsed ? '34px' : '100%',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'var(--hover-tint)'
              e.currentTarget.style.color = 'var(--text-primary)'
              e.currentTarget.style.borderColor = 'var(--border-light)'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'transparent'
              e.currentTarget.style.color = 'var(--text-secondary)'
              e.currentTarget.style.borderColor = 'var(--border)'
            }}
          >
            {Icons.signOut}
            {!collapsed && 'Sign Out'}
          </button>
        </div>
      </aside>

      {/* ── Collapse toggle — desktop only, sibling of <aside> so it isn't
           clipped by the sidebar's overflow:hidden ─────────────────────── */}
      <button
        onClick={toggleCollapsed}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        className="sidebar-collapse-btn"
        style={{
          position: 'absolute',
          top: '50%',
          left: `${sidebarW - 12}px`,
          transform: 'translateY(-50%)',
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          background: 'var(--border)',
          border: '1px solid var(--border-light)',
          color: 'var(--text-secondary)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          transition: 'left 0.25s ease, background 0.12s, color 0.12s, border-color 0.12s',
          flexShrink: 0,
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background = 'var(--accent)'
          e.currentTarget.style.color = '#fff'
          e.currentTarget.style.borderColor = 'var(--accent)'
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = 'var(--border)'
          e.currentTarget.style.color = 'var(--text-secondary)'
          e.currentTarget.style.borderColor = 'var(--border-light)'
        }}
      >
        {collapsed ? Icons.chevronRight : Icons.chevronLeft}
      </button>

      {/* ── Main content ─────────────────────────────────────────────── */}
      <main style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', background: 'var(--bg)', position: 'relative', transition: 'background-color 0.2s ease' }}>
        {/* key remounts this on every route change, which re-triggers the
            .page-transition CSS animation -- a single fade+lift on entry,
            not a scattered per-section effect. */}
        {/* Top-level pages put search beside their title, as the CRM
            Dashboard does; pages without a title row get it above. */}
        {!SEARCH_IN_HEADER.includes(location.pathname.replace(/\/+$/, '')) && (
          <div className="portal-searchbar">
            <PortalSearch />
          </div>
        )}
        <div key={location.pathname} className="page-transition">
          {children}
        </div>
      </main>

    </div>
  )
}

export default function PortalLayout({ children }) {
  return (
    <PortalThemeProvider>
      <PortalLayoutInner>{children}</PortalLayoutInner>
    </PortalThemeProvider>
  )
}
